import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import type { CashflowProvisionDto, CashflowProvisionGroup, CashflowProvisionTitle } from "@bi/types";
import { api } from "../../lib/api";
import { fmtBRL, fmtDateShort } from "../../lib/format";
import {
  addMonths, monthOptions, monthShort, provisionAttention, provisionHeroStatus, provisionMemory, provisionNumbers,
  type ProvisionCardKey,
} from "../../lib/cashflow-provision";
import { KpiTile, SplitMeter } from "../intelligence/KpiTile";
import { AttentionPanel } from "../intelligence/AttentionPanel";
import { SystemHero, HeroNumber } from "../intelligence/SystemHero";
import { QueryErrorBanner } from "../QueryErrorBanner";
import { LoadMoreRow, useProgressiveList } from "../LoadMoreRow";
import { IconAlertTriangle, IconCalculator, IconCalendar, IconChevronDown, IconChevronRight, IconDocument, IconInfo, IconLineChart, IconUser, IconTruck, IconX } from "../icons";

/** Quantos meses aparecem como botão; o resto (até 24) fica no seletor. */
const CHIP_MONTHS = 6;
const MAX_MONTHS = 25;
const RECURRING_KEY = "cashflow-provision-recurring";

function currentMonthSP(): string {
  const p = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit" }).formatToParts(new Date());
  return `${p.find((x) => x.type === "year")!.value}-${p.find((x) => x.type === "month")!.value}`;
}

function axisK(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}k`;
  return String(v);
}

const pctTxt = (v: number) => `${Math.round(v * 100)}%`;

function ShareBar({ pct, color }: { pct: number; color: string }) {
  return (
    <div className="h-1.5 rounded-full bg-surface-muted overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: color }} />
    </div>
  );
}

function CardHead({ icon, title, subtitle, action }: { icon: ReactNode; title: string; subtitle?: ReactNode; action?: ReactNode }) {
  return (
    <div className="px-5 py-4 border-b border-surface-border flex flex-wrap items-start gap-2">
      <span className="mt-0.5 text-gray-400 flex-shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function Notice({ tone = "info", children }: { tone?: "info" | "warning"; children: ReactNode }) {
  const cls = tone === "warning"
    ? { box: "border-warning-100 bg-warning-50", icon: "text-warning-600", text: "text-warning-700" }
    : { box: "border-info-100 bg-info-50", icon: "text-info-600", text: "text-gray-700" };
  const Icon = tone === "warning" ? IconAlertTriangle : IconInfo;
  return (
    <div className={`rounded-lg border px-3 py-2.5 flex gap-2 ${cls.box}`}>
      <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${cls.icon}`} />
      <div className={`text-xs leading-snug ${cls.text}`}>{children}</div>
    </div>
  );
}

/** Interruptor acessível (role="switch"). */
function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={checked} disabled={disabled} onClick={() => onChange(!checked)}
      className="inline-flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500">
      <span className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${checked ? "bg-tm-500" : "bg-gray-300"}`} aria-hidden="true">
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
      </span>
      {label}
    </button>
  );
}

function GroupList({ title, icon, subtitle, groups, color, empty }: {
  title: string; icon: ReactNode; subtitle: string; groups: CashflowProvisionGroup[]; color: string; empty: string;
}) {
  return (
    <div className="card overflow-hidden flex flex-col">
      <CardHead icon={icon} title={title} subtitle={subtitle} />
      {groups.length === 0 ? (
        <p className="px-5 py-6 text-sm text-gray-400 text-center flex-1">{empty}</p>
      ) : (
        <div className="p-5 space-y-3 flex-1">
          {groups.map((g) => (
            <div key={g.name}>
              <div className="flex items-baseline gap-2 text-sm">
                <span className={`truncate ${g.name.startsWith("Outros") ? "text-gray-400" : "text-gray-700"}`} title={g.name}>{g.name}</span>
                <span className="text-[11px] text-gray-400 whitespace-nowrap">{g.count} tít.</span>
                <span className="ml-auto font-semibold text-gray-900 whitespace-nowrap tabular-nums">{fmtBRL(g.amount)}</span>
                <span className="w-10 text-right text-xs text-gray-400">{pctTxt(g.share)}</span>
              </div>
              <div className="mt-1.5"><ShareBar pct={g.share * 100} color={g.name.startsWith("Outros") ? "#CBD5E1" : color} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TitlesTable({ titles, total, kind }: { titles: CashflowProvisionTitle[]; total: number; kind: "receber" | "pagar" }) {
  const list = useProgressiveList(titles);
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] text-sm">
        <thead>
          <tr className="text-xs text-gray-400 border-b border-surface-border">
            <th className="px-5 py-2 text-left font-medium">Data na provisão</th>
            <th className="px-3 py-2 text-left font-medium">Vencimento</th>
            <th className="px-3 py-2 text-left font-medium">{kind === "receber" ? "Cliente" : "Fornecedor"}</th>
            <th className="px-5 py-2 text-right font-medium">Valor em aberto</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-border">
          {titles.length === 0 ? (
            <tr><td colSpan={4} className="px-5 py-6 text-center text-gray-400">Nenhum título {kind === "receber" ? "a receber" : "a pagar"} lançado para o mês.</td></tr>
          ) : list.visible.map((t) => (
            <tr key={t.key} className="text-gray-700 hover:bg-surface-muted/50">
              <td className="px-5 py-2.5 whitespace-nowrap">
                {fmtDateShort(t.date)}
                {t.byExpectedDate && <span className="ml-2 rounded-full bg-info-50 px-2 py-0.5 text-[11px] font-medium text-info-700" title="Data da previsão lançada no ERP, não o vencimento">previsão</span>}
              </td>
              <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">{fmtDateShort(t.dueDate)}</td>
              <td className="px-3 py-2.5 max-w-[320px]">
                <span className="truncate block" title={t.counterpart}>{t.counterpart}</span>
                {t.blocked && <span className="mt-0.5 inline-block rounded-full bg-warning-50 px-2 py-0.5 text-[11px] font-medium text-warning-700">Bloqueado</span>}
              </td>
              <td className="px-5 py-2.5 text-right font-semibold text-gray-900 tabular-nums whitespace-nowrap">{fmtBRL(t.amount)}</td>
            </tr>
          ))}
          <LoadMoreRow list={list} colSpan={4} label="títulos" capped={total > titles.length} />
        </tbody>
      </table>
    </div>
  );
}

const WEEKDAY = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const weekday = (ymd: string) => WEEKDAY[new Date(`${ymd}T12:00:00Z`).getUTCDay()]!;

/**
 * Agenda do mês dia a dia: o que vence a receber e a pagar e o saldo que fica.
 * Por padrão só os dias com título (o resto é saldo parado); um botão mostra
 * todos. Clicar num dia abre os títulos dele ali mesmo.
 */
function DayAgenda({ d, estimated }: { d: CashflowProvisionDto; estimated: boolean }) {
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const withTitles = d.days.filter((x) => x.receivableCount + x.payableCount > 0);
  const rows = all ? d.days : withTitles;
  const closing = (x: CashflowProvisionDto["days"][number]) => (estimated ? x.closingBalanceWithRecurring : x.closingBalance);
  const truncated = d.receivableCount > d.titles.receivable.length || d.payableCount > d.titles.payable.length;

  return (
    <div>
      <div className="px-5 pt-3 pb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600">
        <span>{withTitles.length} dia{withTitles.length === 1 ? "" : "s"} com títulos no mês{estimated ? ". Os recorrentes estimados entram no saldo, divididos pelos dias que faltam" : ""}</span>
        <button type="button" onClick={() => setAll((v) => !v)} className="font-medium text-tm-700 hover:underline">
          {all ? "Mostrar só os dias com títulos" : `Mostrar todos os ${d.days.length} dias`}
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="text-xs text-gray-400 border-y border-surface-border">
              <th className="px-5 py-2 text-left font-medium">Dia</th>
              <th className="px-3 py-2 text-right font-medium">A receber</th>
              <th className="px-3 py-2 text-right font-medium">A pagar</th>
              <th className="px-3 py-2 text-right font-medium">Saldo do dia</th>
              <th className="px-5 py-2 text-right font-medium">Caixa no fim do dia</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-border">
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-6 text-center text-gray-400">Nenhum título lançado para {d.monthLabel}.</td></tr>
            )}
            {rows.map((x) => {
              const isOpen = open === x.date;
              const net = x.receivable - x.payable + (estimated ? x.recurringInflow - x.recurringOutflow : 0);
              const c = closing(x);
              const hasTitles = x.receivableCount + x.payableCount > 0;
              const ins = isOpen ? d.titles.receivable.filter((t) => t.date === x.date) : [];
              const outs = isOpen ? d.titles.payable.filter((t) => t.date === x.date) : [];
              return [
                <tr key={x.date} className={`${x.past ? "text-gray-400" : "text-gray-700"} ${hasTitles ? "cursor-pointer hover:bg-surface-muted/50" : ""} ${isOpen ? "bg-surface-muted/60" : ""}`}
                  onClick={hasTitles ? () => setOpen(isOpen ? null : x.date) : undefined}
                  aria-expanded={hasTitles ? isOpen : undefined}>
                  <td className="px-5 py-2.5 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">
                      {hasTitles ? (isOpen ? <IconChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <IconChevronRight className="w-3.5 h-3.5 text-gray-400" />) : <span className="w-3.5" />}
                      <span className="font-medium text-gray-900">{fmtDateShort(x.date)}</span>
                      <span className="text-xs text-gray-400">{weekday(x.date)}</span>
                      {x.past && <span className="text-[11px] text-gray-400">passou</span>}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                    {x.receivable > 0 ? <span className="font-semibold text-tm-700">{fmtBRL(x.receivable)}</span> : <span className="text-gray-300">—</span>}
                    {x.receivableCount > 0 && <span className="ml-1.5 text-[11px] text-gray-400">{x.receivableCount} tít.</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                    {x.payable > 0 ? <span className="font-semibold text-danger-600">{fmtBRL(x.payable)}</span> : <span className="text-gray-300">—</span>}
                    {x.payableCount > 0 && <span className="ml-1.5 text-[11px] text-gray-400">{x.payableCount} tít.</span>}
                  </td>
                  <td className={`px-3 py-2.5 text-right tabular-nums whitespace-nowrap ${net < 0 ? "text-danger-600" : net > 0 ? "text-gray-900" : "text-gray-300"}`}>
                    {Math.abs(net) < 0.005 ? "—" : `${net > 0 ? "+" : "−"}${fmtBRL(Math.abs(net))}`}
                  </td>
                  <td className={`px-5 py-2.5 text-right tabular-nums whitespace-nowrap font-semibold ${c < 0 ? "text-danger-700" : "text-gray-900"}`}>
                    {fmtBRL(c)}
                  </td>
                </tr>,
                isOpen && (
                  <tr key={`${x.date}-titulos`} className="bg-surface-muted/40">
                    <td colSpan={5} className="px-5 py-3">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {([["A receber", ins, "text-tm-700"], ["A pagar", outs, "text-danger-600"]] as const).map(([label, list, color]) => (
                          <div key={label}>
                            <p className="text-xs font-semibold text-gray-900 mb-1.5">{label} em {fmtDateShort(x.date)}</p>
                            {list.length === 0 ? <p className="text-xs text-gray-400">Nenhum título.</p> : (
                              <ul className="divide-y divide-surface-border rounded-lg bg-white border border-surface-border">
                                {list.map((t) => (
                                  <li key={t.key} className="flex items-baseline gap-2 px-3 py-1.5 text-xs">
                                    <span className="truncate text-gray-700" title={t.counterpart}>{t.counterpart}</span>
                                    {t.byExpectedDate && <span className="rounded-full bg-info-50 px-1.5 py-px text-[10px] font-medium text-info-700" title={`Vencimento ${fmtDateShort(t.dueDate)}; esta é a data da previsão no ERP`}>previsão</span>}
                                    {t.blocked && <span className="rounded-full bg-warning-50 px-1.5 py-px text-[10px] font-medium text-warning-700">Bloqueado</span>}
                                    <span className={`ml-auto font-semibold tabular-nums whitespace-nowrap ${color}`}>{fmtBRL(t.amount)}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                      {truncated && <p className="mt-2 text-[11px] text-gray-500">A lista de títulos do mês foi cortada nos primeiros {d.titles.limit} de cada lado; os totais do dia somam todos.</p>}
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Cartão clicável: abre/fecha a memória de cálculo logo abaixo da grade. */
function CardButton({ cardKey, open, onToggle, children }: {
  cardKey: ProvisionCardKey; open: ProvisionCardKey | null; onToggle: (k: ProvisionCardKey | null) => void; children: ReactNode;
}) {
  const isOpen = open === cardKey;
  return (
    <button type="button" onClick={() => onToggle(isOpen ? null : cardKey)} aria-expanded={isOpen}
      aria-controls="memoria-provisao" title="Ver como este número foi calculado"
      className={`block w-full h-full text-left rounded-xl transition focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500 [&:hover>div]:border-tm-300 ${isOpen ? "ring-2 ring-tm-500" : ""}`}>
      {children}
    </button>
  );
}

type MemoryTab = "calculo" | "compoe" | "documentos";

/**
 * Memória de cálculo de um cartão da provisão, no desenho das abas da memória
 * de cálculo do BI: como foi calculado, quem compõe e os documentos. Os números
 * vêm da mesma resposta da tela, então o total fecha com o cartão.
 */
function ProvisionMemoryPanel({ d, withRecurring, cardKey, onClose }: {
  d: CashflowProvisionDto; withRecurring: boolean; cardKey: ProvisionCardKey; onClose: () => void;
}) {
  const m = provisionMemory(d, withRecurring, cardKey);
  const [tab, setTab] = useState<MemoryTab>("calculo");
  const [side, setSide] = useState<"receber" | "pagar">(m.sides[0]!);
  const tabs: Array<[MemoryTab, string]> = [["calculo", "Como foi calculado"], ["compoe", "Quem compõe"], ["documentos", "Documentos"]];

  return (
    <section id="memoria-provisao" aria-label={`Memória de cálculo: ${m.title}`} className="card overflow-hidden">
      <CardHead icon={<IconCalculator className="w-4 h-4" />} title={`Como foi calculado · ${m.title}`}
        subtitle={`${d.monthLabel} · ${m.formula}`}
        action={
          <button type="button" onClick={onClose} aria-label="Fechar"
            className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-500 hover:bg-surface-muted hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500">
            <IconX className="w-3.5 h-3.5" /> Fechar
          </button>
        } />
      <div className="flex flex-wrap gap-1 border-b border-surface-border px-5" role="tablist" aria-label="Memória de cálculo">
        {tabs.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
            className={`border-b-2 px-3 py-3 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500 ${tab === k ? "border-tm-500 text-tm-700" : "border-transparent text-gray-500 hover:text-gray-900"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "calculo" && (
        <div className="p-5 grid grid-cols-1 lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3 space-y-1.5">
            {m.lines.map((l, i) => l.kind === "total" ? (
              <div key={i} className={`mt-1 flex items-baseline justify-between gap-4 rounded-lg px-3 py-2.5 ${l.value < 0 ? "bg-danger-50" : "bg-tm-50"}`}>
                <span className={`text-sm font-semibold ${l.value < 0 ? "text-danger-700" : "text-tm-800"}`}>= {l.label}</span>
                <span className={`text-base font-bold tabular-nums whitespace-nowrap ${l.value < 0 ? "text-danger-700" : "text-tm-800"}`}>{fmtBRL(l.value)}</span>
              </div>
            ) : (
              <div key={i} className="flex items-baseline justify-between gap-4 px-3 py-1 text-sm">
                <span className="text-gray-700">
                  {l.sign && <span className="inline-block w-4 font-semibold text-gray-400">{l.sign === "+" && l.value < 0 ? "−" : l.sign}</span>}
                  {l.label}
                  {l.hint && <span className="block text-xs text-gray-400 mt-0.5 sm:inline sm:ml-2 sm:mt-0">({l.hint})</span>}
                </span>
                <span className="font-semibold text-gray-900 tabular-nums whitespace-nowrap">{fmtBRL(l.sign ? Math.abs(l.value) : l.value)}</span>
              </div>
            ))}
          </div>
          <div className="lg:col-span-2 rounded-lg bg-surface-muted px-4 py-3">
            <p className="text-xs font-semibold text-gray-900 mb-1.5">Regras do cálculo</p>
            <ul className="space-y-1.5 text-xs text-gray-600 leading-snug">
              {m.rules.map((r, i) => <li key={i} className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" aria-hidden="true" />{r}</li>)}
            </ul>
          </div>
        </div>
      )}

      {tab !== "calculo" && m.sides.length > 1 && (
        <div className="px-5 pt-4">
          <div className="inline-flex gap-1 rounded-lg bg-surface-muted p-1" role="tablist" aria-label="Lado">
            {([["receber", `A receber (${d.receivableCount})`], ["pagar", `A pagar (${d.payableCount})`]] as const).map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={side === k} onClick={() => setSide(k)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500 ${side === k ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {tab === "compoe" && (
        <div className="p-5">
          {(side === "receber" ? d.topReceivable : d.topPayable).length === 0 ? (
            <p className="py-4 text-sm text-gray-400 text-center">Nenhum título {side === "receber" ? "a receber" : "a pagar"} lançado para o mês.</p>
          ) : (
            <div className="space-y-3">
              {(side === "receber" ? d.topReceivable : d.topPayable).map((g) => (
                <div key={g.name}>
                  <div className="flex items-baseline gap-2 text-sm">
                    <span className="truncate text-gray-700" title={g.name}>{g.name}</span>
                    <span className="text-[11px] text-gray-400 whitespace-nowrap">{g.count} tít.</span>
                    <span className="ml-auto font-semibold text-gray-900 whitespace-nowrap tabular-nums">{fmtBRL(g.amount)}</span>
                    <span className="w-10 text-right text-xs text-gray-400">{pctTxt(g.share)}</span>
                  </div>
                  <div className="mt-1.5"><ShareBar pct={g.share * 100} color={g.name.startsWith("Outros") ? "#CBD5E1" : side === "receber" ? "var(--brand)" : "#EF4444"} /></div>
                </div>
              ))}
              {withRecurring && d.recurring.available && (
                <p className="text-xs text-gray-500 pt-1">Os recorrentes estimados não têm cliente ou fornecedor: não aparecem aqui.</p>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "documentos" && (
        <div className="pt-3">
          {side === "receber"
            ? <TitlesTable key={`mr-${d.month}`} titles={d.titles.receivable} total={d.receivableCount} kind="receber" />
            : <TitlesTable key={`mp-${d.month}`} titles={d.titles.payable} total={d.payableCount} kind="pagar" />}
        </div>
      )}
    </section>
  );
}

/**
 * Provisão de caixa de um mês (corrente ou futuro), com o que já está lançado
 * em contas a pagar e a receber e, opcionalmente, os recorrentes estimados pela
 * média realizada. A projeção do Sinersoft continua na tabela própria da tela.
 */
export function ProvisionPanel() {
  const currentMonth = currentMonthSP();
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get("provisao");
  const options = monthOptions(currentMonth, MAX_MONTHS);
  const month = fromUrl && options.includes(fromUrl) ? fromUrl : addMonths(currentMonth, 1);
  const setMonth = (m: string) => {
    const p = new URLSearchParams(params);
    p.set("provisao", m);
    setParams(p, { replace: true });
  };

  const [withRecurring, setWithRecurringState] = useState(false);
  useEffect(() => {
    try { setWithRecurringState(localStorage.getItem(RECURRING_KEY) === "1"); } catch { /* sem storage: começa desligado */ }
  }, []);
  const setWithRecurring = (v: boolean) => {
    setWithRecurringState(v);
    try { localStorage.setItem(RECURRING_KEY, v ? "1" : "0"); } catch { /* só nesta visita */ }
  };

  const [side, setSide] = useState<"receber" | "pagar">("receber");
  const [openCard, setOpenCard] = useState<ProvisionCardKey | null>(null);
  const [view, setView] = useState<"semana" | "dia">("semana");

  const { data: d, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["transport", "cashflow", "provision", month],
    queryFn: () => api.get<CashflowProvisionDto>(`/dashboards/transport/cashflow/provision?month=${month}`),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  const loading = isLoading || (isFetching && d?.month !== month);
  const n = d ? provisionNumbers(d, withRecurring) : null;
  const points = d ? provisionAttention(d, withRecurring) : [];
  const basis = d && d.recurring.basisMonths.length > 0
    ? `${d.recurring.basisMonths[0]} a ${d.recurring.basisMonths[d.recurring.basisMonths.length - 1]}`
    : "";

  const chartData = (d?.weeks ?? []).map((w, i) => ({
    semana: w.label,
    "A receber": w.receivable,
    "A receber (estimado)": n?.estimated ? w.recurringInflow : 0,
    "A pagar": w.payable,
    "A pagar (estimado)": n?.estimated ? w.recurringOutflow : 0,
    "Saldo no fim da semana": n?.weekClosing[i] ?? 0,
  }));

  const headline = !d || !n ? null : d.isCurrentMonth ? (
    <>Em {d.monthLabel} já entraram {fmtBRL(d.realizedInflow)} e saíram {fmtBRL(d.realizedOutflow)}. Até o fim do mês, {n.estimated ? "com os recorrentes estimados," : "com o que está lançado,"} a provisão é de <HeroNumber>{fmtBRL(n.result)}</HeroNumber>.</>
  ) : (
    <>Para {d.monthLabel} {n.estimated ? "estão previstos" : "estão lançados"} {fmtBRL(n.inflow)} a receber e {fmtBRL(n.outflow)} a pagar, provisão de <HeroNumber>{fmtBRL(n.result)}</HeroNumber>.</>
  );
  const detail = !d || !n ? null : `O caixa ${d.isCurrentMonth ? "está hoje em" : "começa o mês em"} ${fmtBRL(n.opening)} e termina ${d.monthLabel.split(" de ")[0]} em ${fmtBRL(n.closing)}.${n.estimated ? ` Inclui ${fmtBRL(d.recurring.monthInflow)} de entradas e ${fmtBRL(d.recurring.monthOutflow)} de saídas estimadas pela média de ${basis}.` : ""}`;

  return (
    <section aria-labelledby="provisao-titulo" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="provisao-titulo" className="font-display font-semibold text-lg text-gray-900">Provisão</h2>
          <p className="text-sm text-gray-600 mt-0.5">Como fica o caixa num mês, com o que já está lançado em contas a receber e a pagar. Clique num indicador para ver como foi calculado</p>
        </div>
        <Switch checked={withRecurring} onChange={setWithRecurring} label="Incluir recorrentes estimados"
          disabled={!!d && !d.recurring.available} />
      </div>

      {/* Escolha do mês: os próximos como botões, o resto num seletor */}
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Mês da provisão">
        {options.slice(0, CHIP_MONTHS).map((m) => (
          <button key={m} type="button" onClick={() => setMonth(m)} aria-pressed={month === m}
            className={`rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500 ${
              month === m ? "bg-tm-500 text-white border-tm-500" : "bg-white text-gray-700 border-surface-border hover:bg-surface-muted"}`}>
            {monthShort(m)}{m === currentMonth && <span className={`ml-1.5 text-xs ${month === m ? "text-white/80" : "text-gray-400"}`}>atual</span>}
          </button>
        ))}
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <IconCalendar className="w-4 h-4 text-gray-400" />
          <span className="sr-only">Outro mês</span>
          <select value={options.slice(0, CHIP_MONTHS).includes(month) ? "" : month} onChange={(e) => e.target.value && setMonth(e.target.value)}
            className="rounded-lg border border-surface-border bg-white px-2 py-2 text-sm text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500">
            <option value="">Outro mês…</option>
            {options.slice(CHIP_MONTHS).map((m) => <option key={m} value={m}>{monthShort(m)}</option>)}
          </select>
        </label>
      </div>

      {isError && <QueryErrorBanner what="a provisão" error={error} onRetry={() => { refetch(); }} retrying={isFetching} />}

      {!isError && (
        <SystemHero loading={loading || !d} status={provisionHeroStatus(points)}
          statusText={points.length > 0 ? `${points.length} ponto${points.length > 1 ? "s" : ""} de atenção` : "nada pede ação"}
          headline={headline} detail={detail} />
      )}

      {!isError && (
        <AttentionPanel points={points} loading={loading || !d}
          empty={{ title: "Nada pede ação neste mês", text: "O saldo projetado não fica negativo em nenhuma semana, o lançado está perto da média e não há concentração nem vencidos fora da conta." }} />
      )}

      {!isError && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <CardButton cardKey="receber" open={openCard} onToggle={setOpenCard}>
            <KpiTile label={n?.estimated ? "A receber no mês (com estimados)" : "A receber no mês"} loading={loading || !d}
            value={n ? fmtBRL(n.inflow) : "—"}
            visual={d && n?.estimated && d.recurring.monthInflow > 0 ? (
              <SplitMeter parts={[
                { label: "Lançado", value: d.receivable, display: fmtBRL(d.receivable), tone: "brand" },
                { label: "Estimado", value: d.recurring.monthInflow, display: fmtBRL(d.recurring.monthInflow), tone: "muted" },
              ]} />
            ) : undefined}
            footnote={d ? `${d.receivableCount} título${d.receivableCount === 1 ? "" : "s"} lançado${d.receivableCount === 1 ? "" : "s"}${d.coverage.inflowPct != null ? `. Cobre ${pctTxt(d.coverage.inflowPct)} da média de entradas` : ""}` : undefined} />
          </CardButton>
          <CardButton cardKey="pagar" open={openCard} onToggle={setOpenCard}>
            <KpiTile label={n?.estimated ? "A pagar no mês (com estimados)" : "A pagar no mês"} loading={loading || !d}
            value={n ? fmtBRL(n.outflow) : "—"}
            visual={d && n?.estimated && d.recurring.monthOutflow > 0 ? (
              <SplitMeter parts={[
                { label: "Lançado", value: d.payable, display: fmtBRL(d.payable), tone: "danger" },
                { label: "Estimado", value: d.recurring.monthOutflow, display: fmtBRL(d.recurring.monthOutflow), tone: "muted" },
              ]} />
            ) : undefined}
            footnote={d ? `${d.payableCount} título${d.payableCount === 1 ? "" : "s"} lançado${d.payableCount === 1 ? "" : "s"}${d.coverage.outflowPct != null ? `. Cobre ${pctTxt(d.coverage.outflowPct)} da média de saídas` : ""}` : undefined} />
          </CardButton>
          <CardButton cardKey="provisao" open={openCard} onToggle={setOpenCard}>
            <KpiTile label="Provisão do mês" loading={loading || !d}
            value={n ? fmtBRL(n.result) : "—"}
            status={n && n.result < 0 ? "critical" : null}
            footnote={d?.isCurrentMonth ? "A receber menos a pagar, de hoje até o fim do mês" : "A receber menos a pagar dentro do mês"} />
          </CardButton>
          <CardButton cardKey="saldo" open={openCard} onToggle={setOpenCard}>
            <KpiTile label="Saldo no fim do mês" loading={loading || !d}
            value={n ? fmtBRL(n.closing) : "—"}
            tone={n && n.closing < 0 ? "critical" : null}
            footnote={n && d ? `${d.isCurrentMonth ? "Saldo bancário de hoje" : "Começa o mês com"} ${fmtBRL(n.opening)}` : undefined} />
          </CardButton>
        </div>
      )}
      {!isError && d && !loading && openCard && (
        <ProvisionMemoryPanel key={openCard} d={d} withRecurring={withRecurring} cardKey={openCard} onClose={() => setOpenCard(null)} />
      )}

      {!isError && d && withRecurring && !d.recurring.available && (
        <Notice tone="warning">Sem pagamentos e recebimentos realizados nos últimos meses fechados para calcular a média. A provisão mostra só o que está lançado.</Notice>
      )}
      {!isError && d && n?.estimated && (
        <Notice>
          <strong>Recorrentes estimados:</strong> média realizada de {basis} ({fmtBRL(d.recurring.avgInflow)} de entradas e {fmtBRL(d.recurring.avgOutflow)} de saídas por mês).
          Cada mês recebe só a diferença entre a média e o que já está lançado{d.isCurrentMonth ? " ou realizado" : ""}, nunca menos que zero, distribuída pelos dias que faltam.
          O estimado aparece em tom claro no gráfico e não é título.
        </Notice>
      )}

      {/* Semanas do mês: onde o caixa aperta */}
      {!isError && (
        <div className="card overflow-hidden">
          <CardHead icon={<IconLineChart className="w-4 h-4" />} title={view === "semana" ? "Semana a semana" : "Dia a dia"}
            subtitle={!d ? "Carregando…" : view === "semana"
              ? `Entradas e saídas por semana de ${d.monthLabel} e o saldo projetado no fim de cada uma${d.isCurrentMonth ? ". Semanas que já passaram estão no saldo de hoje" : ""}`
              : `O que vence a receber e a pagar em cada dia de ${d.monthLabel} e o saldo que fica no fim do dia. Clique num dia para ver os títulos`}
            action={
              <div className="flex gap-1 rounded-lg bg-surface-muted p-1" role="tablist" aria-label="Visão">
                {([["semana", "Por semana"], ["dia", "Por dia"]] as const).map(([k, label]) => (
                  <button key={k} type="button" role="tab" aria-selected={view === k} onClick={() => setView(k)}
                    className={`rounded-md px-3 py-1.5 text-xs font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500 ${view === k ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"}`}>
                    {label}
                  </button>
                ))}
              </div>
            } />
          {view === "dia" ? (
            loading || !d ? <div className="m-5 h-48 rounded-lg bg-gray-50 animate-pulse" /> : <DayAgenda d={d} estimated={!!n?.estimated} />
          ) : (<>
          <div className="px-3 pt-4 pb-2 h-72">
            {loading || !d ? <div className="h-full rounded-lg bg-gray-50 animate-pulse" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="grad-prov-in" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--brand)" stopOpacity={0.95} />
                      <stop offset="100%" stopColor="var(--brand)" stopOpacity={0.55} />
                    </linearGradient>
                    <linearGradient id="grad-prov-out" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#EF4444" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#EF4444" stopOpacity={0.5} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="semana" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                  <YAxis tickFormatter={axisK} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} width={48} />
                  <Tooltip formatter={(v: number, name: string) => [fmtBRL(v), name]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }} />
                  <ReferenceLine y={0} stroke="#9ca3af" />
                  <Bar dataKey="A receber" stackId="in" fill="url(#grad-prov-in)" radius={n?.estimated ? undefined : [4, 4, 0, 0]} />
                  {n?.estimated && <Bar dataKey="A receber (estimado)" stackId="in" fill="var(--brand)" fillOpacity={0.25} radius={[4, 4, 0, 0]} />}
                  <Bar dataKey="A pagar" stackId="out" fill="url(#grad-prov-out)" radius={n?.estimated ? undefined : [4, 4, 0, 0]} />
                  {n?.estimated && <Bar dataKey="A pagar (estimado)" stackId="out" fill="#EF4444" fillOpacity={0.22} radius={[4, 4, 0, 0]} />}
                  <Line type="monotone" dataKey="Saldo no fim da semana" stroke="#1F2937" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
          {/* Legenda própria: a do Recharts não mostra a diferença entre lançado e estimado */}
          <ul className="flex flex-wrap justify-center gap-x-4 gap-y-1 px-5 pb-4 text-xs text-gray-600">
            <li className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-tm-500" aria-hidden="true" />A receber lançado</li>
            {n?.estimated && <li className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-tm-500/25" aria-hidden="true" />A receber estimado</li>}
            <li className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-danger-500" aria-hidden="true" />A pagar lançado</li>
            {n?.estimated && <li className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-danger-500/25" aria-hidden="true" />A pagar estimado</li>}
            <li className="flex items-center gap-1.5"><span className="w-4 h-0.5 bg-gray-800" aria-hidden="true" />Saldo no fim da semana</li>
          </ul>
          </>)}
        </div>
      )}

      {/* Para quem e de quem */}
      {!isError && d && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <GroupList title="A receber por cliente" icon={<IconUser className="w-4 h-4" />}
            subtitle={`Lançado para ${d.monthLabel}, por grupo econômico`} groups={d.topReceivable} color="var(--brand)"
            empty="Nenhum título a receber lançado para o mês." />
          <GroupList title="A pagar por fornecedor" icon={<IconTruck className="w-4 h-4" />}
            subtitle={`Lançado para ${d.monthLabel}`} groups={d.topPayable} color="#EF4444"
            empty="Nenhum título a pagar lançado para o mês." />
        </div>
      )}

      {!isError && d && (d.overdue.receivable > 0 || d.overdue.payable > 0) && (
        <Notice tone="warning">
          <strong>Fora da conta:</strong>{" "}
          {d.overdue.receivable > 0 && <>{fmtBRL(d.overdue.receivable)} a receber vencidos ({d.overdue.receivableCount} títulos)</>}
          {d.overdue.receivable > 0 && d.overdue.payable > 0 && " e "}
          {d.overdue.payable > 0 && <>{fmtBRL(d.overdue.payable)} a pagar vencidos ({d.overdue.payableCount} títulos)</>}
          , sem previsão nova no ERP. Não se sabe quando entram ou saem, por isso não estão em nenhum mês.
        </Notice>
      )}

      {/* Os documentos por trás do número */}
      {!isError && d && (
        <div className="card overflow-hidden">
          <CardHead icon={<IconDocument className="w-4 h-4" />} title={`Títulos de ${d.monthLabel}`}
            subtitle="Em aberto, pela data de previsão quando lançada no ERP, senão pelo vencimento"
            action={
              <div className="flex gap-1 rounded-lg bg-surface-muted p-1" role="tablist" aria-label="Lado">
                {([["receber", `A receber (${d.receivableCount})`], ["pagar", `A pagar (${d.payableCount})`]] as const).map(([k, label]) => (
                  <button key={k} type="button" role="tab" aria-selected={side === k} onClick={() => setSide(k)}
                    className={`rounded-md px-3 py-1.5 text-xs font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500 ${side === k ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"}`}>
                    {label}
                  </button>
                ))}
              </div>
            } />
          {side === "receber"
            ? <TitlesTable key={`r-${d.month}`} titles={d.titles.receivable} total={d.receivableCount} kind="receber" />
            : <TitlesTable key={`p-${d.month}`} titles={d.titles.payable} total={d.payableCount} kind="pagar" />}
          {(d.receivableCount > d.titles.limit || d.payableCount > d.titles.limit) && (
            <p className="px-5 py-3 text-[11px] text-gray-500 border-t border-surface-border">
              A lista mostra os {d.titles.limit} primeiros por data de cada lado. Os totais acima somam todos.
            </p>
          )}
          {d.warnings.length > 0 && (
            <div className="px-5 py-3 border-t border-surface-border space-y-1">
              {d.warnings.map((w, i) => (
                <p key={i} className="text-[11px] text-gray-500 flex items-start gap-1.5">
                  <IconInfo className="w-3.5 h-3.5 flex-shrink-0 mt-px" />{w}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
